import { nanoid } from "@reduxjs/toolkit";
import { MutableRefObject, useEffect, useRef } from "react";
import LoadingLabel from "../../../components/LoadingLabel";
import { useView } from "../../../hooks/useView";
import { Entity } from "../../../models/viz/Entity";
import { FragmentsMap } from "../../playback/components/useLoadFragments";
import "./Timeline.scss";
import useTimeline, { TimelineLabel } from "./useTimeline";
import useTimelineEvents from "./useTimelineEvents";
import useTimelineWidth from "./useTimelineWidth";
import { MediaSourceWithExtras } from "../../../store/media/MediaItems";
import {
    useGetAllTagEventsQuery,
    useGetStoredFilesForMediaQuery,
    useGetVideoClipsQuery,
} from "../../../store/api/kinesense";
import { TagEvent } from "../../../models/tags/TagEvent";
import useUserConfig from "../../../hooks/useUserConfig";

/** Used to limit the maximum (default) duration to display on the timeline.
 *
 * This prevents a long video from starting off too zoomed out on the timeline.*/
export const MAX_DURATION_MS = 3 * 60 * 60 * 1000;

export interface TimelineProps {
    viewId: string;
    media: MediaSourceWithExtras;
    isLoaded: boolean;
    videoRef: MutableRefObject<HTMLVideoElement>;
    entities: Entity[];
    fragmentsMap: FragmentsMap;
}

function Timeline(props: TimelineProps) {
    const { view } = useView(props.viewId);
    const { userConfig } = useUserConfig();
    const isShowingDebugUi = userConfig?.showDebugUi;

    const timelineId = useRef(`tl-${nanoid(6)}`);
    const timelineRef = useRef<SVGSVGElement | undefined>(undefined);

    const { totalWidth, calculateTimelineWidth, updateTimelineWidth } = useTimelineWidth(timelineRef);
    const labels = useRef<TimelineLabel[]>([]);

    const projectId = view?.projectId;
    const mediaId = props.media?.mediaId;

    const { data: storedFiles, isSuccess: hasLoadedStoredFiles } = useGetStoredFilesForMediaQuery(
        { projectId: props.media?.projectId, mediaId: props.media?.mediaId },
        { skip: props.media === undefined },
    );

    const { data: clips, isSuccess: hasLoadedClips } = useGetVideoClipsQuery(
        { projectId, mediaId },
        { skip: !projectId || !mediaId },
    );
    const { data: tags, isSuccess: hasLoadedTags } = useGetAllTagEventsQuery(
        { projectId },
        { skip: !projectId || !mediaId },
    );

    const {
        domainOffset,
        secondsPerPixel,
        xScale,
        entities,
        drawTimeline,
        setTimelineAttributes,
        calculateDomainStart,
        calculateDomainEnd,
        calculateCentreTime,
        updateEntities,
    } = useTimeline({
        fragmentsMap: props.fragmentsMap,
        isShowingDebugUi,
        media: props.media,
        timelineRef,
        totalWidth,
        labels,
        getDefaultDuration,
    });

    const { setListeners, onTimelineMouseEnter, onTimelineMouseLeave, updateCursors, setMarkers, cursorMarker } =
        useTimelineEvents(
            view,
            props.viewId,
            props.media,
            timelineId,
            timelineRef,
            props.videoRef,
            xScale,
            domainOffset,
            secondsPerPixel,
            getDefaultSecondsPerPixel,
            totalWidth,
            drawTimeline,
        );

    function resetZoom() {
        secondsPerPixel.current = getDefaultSecondsPerPixel();
    }

    function getDefaultDuration() {
        return Math.min(props.media.duration, MAX_DURATION_MS);
    }

    function getDefaultSecondsPerPixel() {
        const paddingMultiplier = 1.05;
        return (getDefaultDuration() / 1000 / totalWidth.current) * paddingMultiplier;
    }

    function resetPosition() {
        if (xScale.current === undefined) {
            return;
        }

        domainOffset.current = 0;

        const centrePoint = 0.5;
        const centreTime = calculateCentreTime(
            props.media.startsAt,
            props.media.startsAt + getDefaultDuration(),
            centrePoint,
        );

        xScale.current.domain([
            calculateDomainStart(centreTime, centrePoint),
            calculateDomainEnd(centreTime, centrePoint),
        ]);
    }

    /** Shifts the timeline's view either forwards or backwards - used for keeping the cursor in view */
    function shiftPosition(direction: "forward" | "backward") {
        const currentDomain = xScale.current.domain();
        const startTime = currentDomain[0].getTime();
        const endTime = currentDomain[1].getTime();
        const frameWidthMilliseconds = endTime - startTime;
        const paddingMilliseconds = frameWidthMilliseconds * 0.05;
        const cursorMilliseconds = view.cursor;

        const centrePoint = 0.5;

        // Limit the shift position based on media start and end times
        const minStart = props.media.startsAt - paddingMilliseconds;
        const maxEnd = props.media.endsAt + paddingMilliseconds;

        let centreTime: number;
        switch (direction) {
            case "forward": {
                const end = Math.min(maxEnd, cursorMilliseconds - paddingMilliseconds + frameWidthMilliseconds);
                centreTime = calculateCentreTime(end - frameWidthMilliseconds, end, centrePoint);
                break;
            }
            case "backward": {
                const start = Math.max(cursorMilliseconds - frameWidthMilliseconds / 2, minStart);
                centreTime = calculateCentreTime(start, start + frameWidthMilliseconds, centrePoint);
                break;
            }
        }

        domainOffset.current = 0;
        xScale.current.domain([
            calculateDomainStart(centreTime, centrePoint),
            calculateDomainEnd(centreTime, centrePoint),
        ]);

        drawTimeline();
        updateCursors();
    }

    /** Update the timeline's view based on the cursor's position */
    function updateViewOnCursor() {
        if (view.cursor > xScale.current?.domain?.()[1].getTime()) {
            shiftPosition("forward");
        } else if (view.cursor < xScale.current?.domain?.()[0].getTime()) {
            shiftPosition("backward");
        }
    }

    // Resize and update timeline on resize events
    useEffect(() => {
        function updateWidthHandler() {
            // Return early if no update to the timeline width occurred
            if (!props.media || !updateTimelineWidth()) {
                return;
            }

            // Reset zoom level
            resetZoom();
            resetPosition();

            setTimelineAttributes();
            drawTimeline();
            updateCursors();
        }

        window.addEventListener("resize", updateWidthHandler);

        return () => {
            window.removeEventListener("resize", updateWidthHandler);
        };
    }, [props.media?.name]);

    // Setup and update timeline based on load state, cursor positions and entities
    useEffect(() => {
        if (!props.media?.startsAt || !props.isLoaded) {
            return;
        }

        if (view?.cursor !== undefined && cursorMarker.current !== undefined) {
            updateCursors();
            updateViewOnCursor();
        }

        // Listeners need to be reset
        setListeners();

        if (entities.current !== undefined) {
            // Exit early if entities is empty and timeline has already been rendered
            if (!props.entities?.length && entities.current.length == 0) {
                return;
            }

            // Exit early if there has been no change to entities
            if (props.entities.length == entities.current.length) {
                const ids = new Set();
                for (const entity of props.entities) {
                    ids.add(entity.id);
                }
                for (const entity of entities.current) {
                    ids.add(entity.id);
                }
                if (ids.size == entities.current.length) {
                    // Only re-draw timeline if showing debug UI, as otherwise no changes are necessary
                    if (isShowingDebugUi) {
                        drawTimeline();
                    }
                    return;
                }
            }
        }

        totalWidth.current = calculateTimelineWidth();
        resetZoom();

        updateEntities(props.entities);

        setTimelineAttributes();
        drawTimeline();
        setMarkers();
    }, [props, view?.cursor, view?.secondaryCursor]);

    useEffect(() => {
        const labelEntries: TimelineLabel[] = [];

        if (hasLoadedTags && tags.length != 0) {
            labelEntries.push(
                ...tags
                    .filter((tag) => tag.occurrences.some((o) => o.mediaId == mediaId))
                    .map((tag) => {
                        return {
                            type: "tag",
                            title: TagEvent.createSummary(tag),
                            startsAt: tag.startsAt,
                            endsAt: tag.startsAt + tag.duration,
                            position: "top",
                        } as const;
                    }),
            );
        }

        if (hasLoadedClips && clips.length != 0) {
            labelEntries.push(
                ...clips.map((clip) => {
                    return {
                        type: "clip",
                        title: clip.name,
                        startsAt: props.media.startsAt + clip.startAt,
                        endsAt: props.media.startsAt + clip.endAt,
                        position: "top",
                    } as const;
                }),
            );
        }

        if (props.media !== undefined) {
            labelEntries.push(
                ...props.media.displayFiles.map((file) => {
                    const fileKey = storedFiles?.get(file.originalFileId)?.key;
                    const fileName = fileKey ? fileKey.split("/").pop() : file.originalFileId;

                    return {
                        type: "video",
                        title: fileName,
                        startsAt: props.media.startsAt + file.startsAt,
                        endsAt: props.media.startsAt + file.startsAt + file.duration,
                        position: "bottom",
                    } as const;
                }),
            );
        }

        if (labels.current.length == 0 && labelEntries.length == 0) {
            return;
        }

        labels.current = labelEntries;

        if (entities.current !== undefined && props.media !== undefined) {
            drawTimeline();
        }
    }, [clips, tags, storedFiles]);

    return (
        <>
            <svg
                className="timeline"
                ref={timelineRef}
                onMouseEnter={onTimelineMouseEnter}
                onMouseLeave={onTimelineMouseLeave}
                role="application"
                aria-label="video timeline"
            />

            <LoadingLabel isLoading={!props.isLoaded} />
        </>
    );
}

export default Timeline;
